import { useCallback, useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { datadogRum } from '@datadog/browser-rum';
import { useCookieConsent } from '../../../utils/custom-hooks/use-cookie-consent.hook';
import { useGetLanguage } from '../../../utils/useGetLanguage.hook';

export const useDatadogRum = () => {
  const { accountsCws15893EnableCookieConsent, farmSettingsCws18526EnableDatadogTraceUrls } =
    useFlags();
  const { cookieBotConsentStatistics } = useCookieConsent();
  const { currentLanguage } = useGetLanguage();

  const getHasUserConsent = useCallback(() => {
    if (accountsCws15893EnableCookieConsent) {
      return cookieBotConsentStatistics;
    }
    return true;
  }, [accountsCws15893EnableCookieConsent, cookieBotConsentStatistics]);

  useEffect(() => {
    const applicationId = process.env.REACT_APP_DATADOG_APP_ID;
    const clientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN;

    if (applicationId && clientToken) {
      datadogRum.init({
        applicationId,
        clientToken,
        site: 'datadoghq.eu',
        service: 'web-react-cropwise-base',
        env: process.env.REACT_APP_envName,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 0,
        defaultPrivacyLevel: 'mask-user-input',
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        enablePrivacyForActionName: false,
        compressIntakeRequests: true,
        ...(farmSettingsCws18526EnableDatadogTraceUrls && {
          allowedTracingUrls: [process.env.REACT_APP_API_URL, process.env.REACT_APP_ACCOUNTS_URL]
        })
      });
    }
  }, [farmSettingsCws18526EnableDatadogTraceUrls]);

  const setGlobalDataDD = useCallback(() => {
    datadogRum.setGlobalContextProperty('navigation', {
      browser_language: currentLanguage || navigator.language
    });
  }, [currentLanguage]);

  const setUserDataDD = useCallback(
    (userData: {
      id: string;
      email: string;
      name: string;
      country: string;
      locale: string;
      login: string;
    }) => {
      const hasUserConsent = getHasUserConsent();
      if (!hasUserConsent) return;

      datadogRum.setUser({
        id: userData.id,
        email: userData.email,
        name: userData.name,
        country: userData.country,
        locale: userData.locale,
        login: userData.login
      });
    },
    [getHasUserConsent]
  );

  return {
    datadogRum,
    setGlobalDataDD,
    setUserDataDD
  };
};
